import React, { useState, useEffect, useMemo } from 'react';
import { 
    Box, Typography, Paper, TextField, Button, 
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
    Alert, Stack, IconButton, Chip, Dialog, DialogTitle, DialogContent,
    DialogActions, FormControl, InputLabel, Select, MenuItem, TableSortLabel,
    Accordion, AccordionSummary, AccordionDetails, Divider,
    Checkbox, FormControlLabel, RadioGroup, Radio, FormLabel,
    Autocomplete, List, ListItem, ListItemText, InputAdornment
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import LanguageIcon from '@mui/icons-material/Language';
import SecurityIcon from '@mui/icons-material/Security';
import TimerIcon from '@mui/icons-material/Timer';
import AddIcon from '@mui/icons-material/Add';
import { configService, ticketService } from '../api';
import AppDialogTitle from './AppDialogTitle';
import { useTranslation } from 'react-i18next';

const Configuration = () => {
    const { t } = useTranslation();
    const [statuses, setStatuses] = useState([]);
    const [newStatus, setNewStatus] = useState({ name: '', key: '', color: '#1976d2', is_initial: false });
    const [editingId, setEditingId] = useState(null);
    const [message, setMessage] = useState({ text: '', type: '' });
    
    const [projects, setProjects] = useState([]);
    const [newProject, setNewProject] = useState({ name: '' });
    const [editingProjectId, setEditingProjectId] = useState(null);

    const [slas, setSlas] = useState([]);
    const [slaValues, setSlaValues] = useState({
        LOW: 24,
        MEDIUM: 24,
        HIGH: 24,
        CRITICAL: 24
    });
    
    const [assignmentStrategy, setAssignmentStrategy] = useState('VOLUME');
    const [agents, setAgents] = useState([]);
    const [allowedDomains, setAllowedDomains] = useState('');
    const [projectAssignments, setProjectAssignments] = useState({}); // { projectId: [agentIds] }
    const [isSavingAssignment, setIsSavingAssignment] = useState(false);
    const [selectedProjectForStatus, setSelectedProjectForStatus] = useState('all');

    const [rolePermissions, setRolePermissions] = useState([]);
    const [newRoleName, setNewRoleName] = useState('');
    const [isSavingRoles, setIsSavingRoles] = useState(false);
    
    const [openDeleteRole, setOpenDeleteRole] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);
    const [targetRoleKey, setTargetRoleKey] = useState('');

    const [isSavingSLA, setIsSavingSLA] = useState(false);

    const [openProjectDialog, setOpenProjectDialog] = useState(false);
    const [projectFuncs, setProjectFuncs] = useState([]);
    const [newFuncName, setNewFuncName] = useState('');
    const [isSavingFunc, setIsSavingFunc] = useState(false);
    const [editingFuncId, setEditingFuncId] = useState(null);
    const [editingFuncName, setEditingFuncName] = useState('');
    const [funcSearchTerm, setFuncSearchTerm] = useState('');
    const [funcSortBy, setFuncSortBy] = useState('date'); // 'date' o 'name'

    // Estado para el ordenamiento de la tabla de proyectos
    const [projectSort, setProjectSort] = useState({ field: 'name', order: 'asc' });

    const handleProjectSort = (field) => {
        const isAsc = projectSort.field === field && projectSort.order === 'asc';
        setProjectSort({ field, order: isAsc ? 'desc' : 'asc' });
    };

    const sortedProjects = useMemo(() => {
        return [...projects].sort((a, b) => {
            let aValue = a[projectSort.field];
            let bValue = b[projectSort.field];

            if (projectSort.field === 'created_at' || projectSort.field === 'updated_at') {
                aValue = aValue ? new Date(aValue) : 0;
                bValue = bValue ? new Date(bValue) : 0;
            } else {
                aValue = (aValue || '').toString().toLowerCase();
                bValue = (bValue || '').toString().toLowerCase();
            }

            if (aValue < bValue) return projectSort.order === 'asc' ? -1 : 1;
            if (aValue > bValue) return projectSort.order === 'asc' ? 1 : -1;
            return 0;
        });
    }, [projects, projectSort]);

    // Estado para controlar qué acordeón está abierto y cambiar el color de su icono
    const [expandedPanels, setExpandedPanels] = useState({
        projects: true, // El de proyectos inicia abierto por defecto
        statuses: false,
        assignment: false,
        domains: false,
        roles: false,
        sla: false
    });

    const handleAccordionToggle = (panel) => (event, isExpanded) => {
        setExpandedPanels(prev => ({ ...prev, [panel]: isExpanded }));
    };

    // Estado para la migración masiva
    const [openMigrate, setOpenMigrate] = useState(false);
    const [migration, setMigration] = useState({ from: '', to: '' });
    const [isMigrating, setIsMigrating] = useState(false);
    const [confirmText, setConfirmText] = useState('');

    const getFriendlyErrorMessage = (err, fallback) => {
        if (err.response) {
            const status = err.response.status;
            const data = err.response.data;

            if (status === 400 && data) {
                if (typeof data === 'object') {
                    // Extraer el primer mensaje de error de cualquier campo (ej: 'name', 'non_field_errors')
                    const firstError = Object.values(data)[0];
                    if (Array.isArray(firstError)) return firstError[0];
                    if (typeof firstError === 'string') return firstError;
                    return data.detail || data.error || fallback;
                }
                return typeof data === 'string' ? data : fallback;
            }
            
            if (status === 401) {
                return "Su sesión ha expirado. Por favor, inicie sesión nuevamente.";
            }
            // Error de permisos (403)
            if (status === 403) {
                return "No tienes permisos de Administrador para realizar esta acción.";
            }
            // Si es un error del servidor (500+), ocultamos los detalles técnicos
            if (status >= 500) return "Error interno del servidor. Por favor, intente más tarde.";
        }
        return fallback;
    };

    useEffect(() => {
        fetchProjects();
        fetchSLAs();
        fetchAgents();
        fetchRoles();
        fetchAssignmentConfig();
    }, []);

    // Cargar estados cuando cambie el proyecto seleccionado
    useEffect(() => {
        fetchStatuses(selectedProjectForStatus);
    }, [selectedProjectForStatus]);

    const fetchStatuses = async (projectId) => {
        try {
            const param = projectId === 'all' ? '' : projectId;
            const res = await configService.getStatuses(param);
            const data = res.data.results || res.data;
            setStatuses(data);
        } catch (err) {
            console.error("Failed to fetch statuses", err);
        }
    };

    const fetchProjects = async () => {
        try {
            const res = await configService.getProjects();
            const data = res.data.results || res.data;
            setProjects(data);
        } catch (err) {
            console.error("Failed to fetch projects", err);
        }
    };

    const fetchAgents = async () => {
        try {
            const res = await ticketService.getAgents();
            setAgents(res.data.results || res.data);
        } catch (err) {
            console.error("Failed to fetch agents", err);
        }
    };

    const fetchSLAs = async () => {
        try {
            const res = await configService.getSLAs();
            const data = res.data.results || res.data;
            setSlas(data);
            
            const newValues = {
                LOW: 24,
                MEDIUM: 24,
                HIGH: 24,
                CRITICAL: 24
            };
            data.forEach(item => {
                newValues[item.priority] = item.hours;
            });
            setSlaValues(newValues);
        } catch (err) {
            console.error("Failed to fetch SLAs", err);
        }
    };

    const fetchAssignmentConfig = async () => {
        try {
            const res = await configService.getAssignmentConfig();
            setAssignmentStrategy(res.data.strategy);
            setAllowedDomains(res.data.allowed_domains || '');
            setProjectAssignments(res.data.project_assignments);
        } catch (err) {
            console.error("Failed to fetch assignment config", err);
        }
    };

    const fetchProjectFunctionalities = async (projectId) => {
        try {
            const res = await configService.getFunctionalities(projectId);
            const data = res.data.results || res.data;
            setProjectFuncs(data);
        } catch (err) {
            console.error("Failed to fetch functionalities", err);
        }
    };

    const fetchRoles = async () => {
        try {
            const res = await configService.getRoles();
            setRolePermissions(res.data.results || res.data);
        } catch (err) {
            console.error("Failed to fetch roles", err);
        }
    };

    const handleCreateOrUpdate = async (e) => {
        e.preventDefault();
        try {
            // Asegurar que el proyecto sea un ID válido o null, nunca una cadena vacía
            const statusData = { 
                ...newStatus, 
                project: selectedProjectForStatus === 'all' ? null : selectedProjectForStatus 
            };
            if (editingId) {
                await configService.updateStatus(editingId, statusData);
                setMessage({ text: 'Estado actualizado correctamente', type: 'success' });
            } else {
                await configService.createStatus(statusData);
                setMessage({ text: 'Estado creado correctamente', type: 'success' });
            }
            setNewStatus({ name: '', key: '', color: '#1976d2', is_initial: false });
            setEditingId(null);
            fetchStatuses(selectedProjectForStatus);
        } catch (err) {
            const errorMsg = getFriendlyErrorMessage(err, 'Error al procesar el estado. Verifique los datos.');
            setMessage({ text: errorMsg, type: 'error' });
        }
    };

    const handleEdit = (status) => {
        setNewStatus({ name: status.name, key: status.key, color: status.color, is_initial: status.is_initial });
        setEditingId(status.id);
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Estás seguro de eliminar este estado?')) {
            try {
                await configService.deleteStatus(id);
                fetchStatuses(selectedProjectForStatus);
                setMessage({ text: 'Estado eliminado', type: 'success' });
            } catch (err) {
                // Capturamos el mensaje de error detallado enviado por el backend
                const backendError = err.response?.status === 400 ? err.response?.data?.error : null;
                setMessage({ text: backendError || 'Error al eliminar el estado.', type: 'error' });
            }
        }
    };

    const handleAddFunctionality = async () => {
        const trimmedName = newFuncName.trim();
        if (!trimmedName) {
            setMessage({ text: 'El nombre de la funcionalidad no puede estar vacío', type: 'error' });
            return;
        }
        if (!editingProjectId) return;

        setIsSavingFunc(true);
        try {
            await configService.createFunctionality({ project: editingProjectId, name: trimmedName });
            setNewFuncName('');
            await fetchProjectFunctionalities(editingProjectId);
            setMessage({ text: 'Funcionalidad añadida correctamente', type: 'success' });
        } catch (err) {
            console.error("Error adding functionality:", err); // Añadido para depuración
            const errorMsg = getFriendlyErrorMessage(err, 'Error al añadir funcionalidad');
            setMessage({ text: errorMsg, type: 'error' });
        } finally {
            setIsSavingFunc(false);
        }
    };

    const handleUpdateFunctionality = async (id) => {
        const trimmedName = editingFuncName.trim();
        if (!trimmedName) {
            setMessage({ text: 'El nombre de la funcionalidad no puede estar vacío', type: 'error' });
            return;
        }

        try {
            await configService.updateFunctionality(id, { name: trimmedName });
            setEditingFuncId(null);
            setEditingFuncName('');
            await fetchProjectFunctionalities(editingProjectId);
            setMessage({ text: 'Funcionalidad actualizada correctamente', type: 'success' });
        } catch (err) {
            const errorMsg = getFriendlyErrorMessage(err, 'Error al actualizar funcionalidad');
            setMessage({ text: errorMsg, type: 'error' });
        }
    };

    // Lógica de filtrado y ordenamiento local
    const filteredAndSortedFuncs = useMemo(() => {
        let result = [...projectFuncs];

        // Filtrar por búsqueda
        if (funcSearchTerm.trim()) {
            result = result.filter(f => f.name.toLowerCase().includes(funcSearchTerm.toLowerCase()));
        }

        // Ordenar
        return result.sort((a, b) => {
            if (funcSortBy === 'name') return a.name.localeCompare(b.name);
            return b.id - a.id; // Fecha de creación (ID más alto primero)
        });
    }, [projectFuncs, funcSearchTerm, funcSortBy]);

    const handleDeleteFunctionality = async (id) => {
        if (!window.confirm('¿Estás seguro de que deseas eliminar esta funcionalidad?')) return;
        try {
            await configService.deleteFunctionality(id);
            fetchProjectFunctionalities(editingProjectId);
        } catch (err) {
            const errorMsg = getFriendlyErrorMessage(err, 'Error al eliminar funcionalidad');
            setMessage({ text: errorMsg, type: 'error' });
        }
    };

    const handleProjectSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingProjectId) {
                await configService.updateProject(editingProjectId, newProject);
                setMessage({ text: 'Proyecto actualizado correctamente', type: 'success' });
            } else {
                const res = await configService.createProject(newProject);
                const createdProject = res.data;
                setEditingProjectId(createdProject.id);
                setMessage({ text: 'Proyecto creado. Ahora puede añadir funcionalidades.', type: 'success' });
                fetchProjects(); // Refrescar la tabla de fondo
                return; 
            }
            setNewProject({ name: '' });
            setEditingProjectId(null);
            setOpenProjectDialog(false);
            fetchProjects();
        } catch (err) {
            console.error("Error submitting project:", err);
            const errorMsg = getFriendlyErrorMessage(err, 'Error al procesar el proyecto.');
            setMessage({ text: errorMsg, type: 'error' });
        }
    };

    const handleDeleteProject = async (id) => {
        if (window.confirm('¿Eliminar este proyecto?')) {
            try {
                await configService.deleteProject(id);
                fetchProjects();
                setMessage({ text: 'Proyecto eliminado', type: 'success' });
            } catch (err) {
                setMessage({ text: 'No se pudo eliminar el proyecto.', type: 'error' });
            }
        }
    };

    const handleSaveSLA = async () => {
        setIsSavingSLA(true);
        setMessage({ text: '', type: '' });
        try {
            // Obtenemos la lista más reciente de SLAs para comparar IDs
            const currentSlasRes = await configService.getSLAs();
            const currentSlas = currentSlasRes.data.results || currentSlasRes.data;

            const promises = Object.entries(slaValues).map(([priority, hours]) => {
                const existingSla = currentSlas.find(s => s.priority === priority);
                if (existingSla) {
                    return configService.updateSLA(existingSla.id, { hours: parseInt(hours, 10) });
                } else {
                    return configService.createSLA({ priority, hours: parseInt(hours, 10) });
                }
            });
            
            await Promise.all(promises);
            setMessage({ text: 'Tiempos de respuesta (SLA) actualizados correctamente. Los cambios solo aplicarán a nuevos tickets.', type: 'success' });
            fetchSLAs();
        } catch (err) {
            setMessage({ text: 'Error al actualizar los tiempos de respuesta.', type: 'error' });
        } finally {
            setIsSavingSLA(false);
        }
    };

    const handleBulkMigrate = async () => {
        if (!migration.from || !migration.to) return;
        if (migration.from === migration.to) {
            alert("El estado de origen y destino no pueden ser iguales.");
            return;
        }

        setIsMigrating(true);
        try {
            const res = await ticketService.bulkMigrateStatus(migration.from, migration.to);
            setMessage({ text: res.data.message, type: 'success' });
            setOpenMigrate(false);
            setMigration({ from: '', to: '' });
            setConfirmText('');
        } catch (err) {
            setMessage({ text: 'Error durante la migración de tickets.', type: 'error' });
        } finally {
            setIsMigrating(false);
        }
    };

    const handleSaveAssignment = async () => {
        setIsSavingAssignment(true);
        try {
            await configService.updateAssignmentConfig({
                strategy: assignmentStrategy,
                project_assignments: projectAssignments,
                allowed_domains: allowedDomains
            });
            setMessage({ text: 'Configuración de asignación guardada correctamente.', type: 'success' });
        } catch (err) {
            setMessage({ text: getFriendlyErrorMessage(err, 'Error al guardar la configuración de asignación.'), type: 'error' });
        } finally {
            setIsSavingAssignment(false);
        }
    };

    const handleCreateRole = async () => {
        if (!newRoleName) return;
        try {
            const roleKey = newRoleName.toUpperCase().replace(/\s+/g, '_');
            await configService.createRole({ role_name: newRoleName, role_key: roleKey });
            setNewRoleName('');
            fetchRoles();
            setMessage({ text: 'Rol creado exitosamente', type: 'success' });
        } catch (err) {
            const errorMsg = getFriendlyErrorMessage(err, 'Error al crear el rol');
            setMessage({ text: errorMsg, type: 'error' });
        }
    };

    const handleTogglePermission = async (role, field) => {
        try {
            const updatedRole = { ...role, [field]: !role[field] };
            await configService.updateRole(role.id, updatedRole);
            fetchRoles();
        } catch (err) {
            setMessage({ text: 'Error al actualizar permiso', type: 'error' });
        }
    };

    const handleDeleteRole = (role) => {
        setRoleToDelete(role);
        setTargetRoleKey('');
        setOpenDeleteRole(true);
    };

    const handleConfirmDeleteRole = async () => {
        try {
            if (roleToDelete.user_count > 0) {
                await configService.migrateAndDeleteRole(roleToDelete.id, targetRoleKey);
            } else {
                await configService.deleteRole(roleToDelete.id);
            }
            setOpenDeleteRole(false);
            fetchRoles();
            setMessage({ text: 'Rol eliminado correctamente', type: 'success' });
        } catch (err) {
            setMessage({ text: 'Error al procesar la eliminación', type: 'error' });
        }
    };

    const moduleList = [
        { key: 'can_access_dashboard', label: t('nav_overview') },
        { key: 'can_access_tickets', label: t('nav_tickets') },
        { key: 'can_access_portal_submit', label: t('nav_submit') },
        { key: 'can_access_portal_followup', label: t('nav_follow') },
        { key: 'can_access_users', label: t('nav_users') },
        { key: 'can_access_config', label: t('nav_config') },
    ];

    return (
      <Box sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h5" fontWeight="bold">
            Configuración Global
          </Typography>
          <Button
            variant="outlined"
            startIcon={<SwapHorizIcon />}
            onClick={() => setOpenMigrate(true)}
          >
            Migrar Tickets entre Estados
          </Button>
        </Stack>

        {message.text && (
          <Alert
            severity={message.type}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ text: "", type: "" })}
          >
            {message.text}
          </Alert>
        )}

        {/* Sección de Proyectos - Ahora en primer lugar */}
        <Accordion
          sx={{
            mb: 2,
            borderRadius: "12px !important",
            "&:before": { display: "none" },
          }}
          elevation={2}
          expanded={expandedPanels.projects}
          onChange={handleAccordionToggle('projects')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <AccountTreeIcon color={expandedPanels.projects ? "primary" : "action"} />
              <Typography variant="h6" fontWeight="600">
                Proyectos Asociados
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={() => { 
                        setEditingProjectId(null); 
                        setNewProject({ name: '' }); 
                        setProjectFuncs([]); 
                        setEditingFuncId(null);
                        setEditingFuncName('');
                        setOpenProjectDialog(true); 
                    }}
                >
                    Agregar Proyecto
                </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ bgcolor: "action.hover" }}>
                  <TableRow>
                    <TableCell>
                        <TableSortLabel
                            active={projectSort.field === 'name'}
                            direction={projectSort.field === 'name' ? projectSort.order : 'asc'}
                            onClick={() => handleProjectSort('name')}
                        >
                            Nombre del Proyecto
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>
                        <TableSortLabel
                            active={projectSort.field === 'created_at'}
                            direction={projectSort.field === 'created_at' ? projectSort.order : 'asc'}
                            onClick={() => handleProjectSort('created_at')}
                        >
                            Fecha Creación
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>
                        <TableSortLabel
                            active={projectSort.field === 'updated_at'}
                            direction={projectSort.field === 'updated_at' ? projectSort.order : 'asc'}
                            onClick={() => handleProjectSort('updated_at')}
                        >
                            Última Modificación
                        </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedProjects.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell fontWeight="500">{p.name}</TableCell>
                      <TableCell variant="body2" color="textSecondary">
                        {p.created_at ? new Date(p.created_at).toLocaleString() : '-'}
                      </TableCell>
                      <TableCell variant="body2" color="textSecondary">
                        {p.updated_at ? new Date(p.updated_at).toLocaleString() : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => {
                            setEditingProjectId(p.id);
                            setNewProject({ name: p.name });
                            setEditingFuncId(null);
                            setEditingFuncName('');
                            setOpenProjectDialog(true);
                            fetchProjectFunctionalities(p.id);
                          }}
                          size="small"
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteProject(p.id)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {projects.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        No hay proyectos configurados
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Sección de Estados */}
        <Accordion
          sx={{
            mb: 2,
            borderRadius: "12px !important",
            "&:before": { display: "none" },
          }}
          elevation={2}
          expanded={expandedPanels.statuses}
          onChange={handleAccordionToggle('statuses')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PlaylistAddCheckIcon color={expandedPanels.statuses ? "primary" : "action"} />
              <Typography variant="h6" fontWeight="600">
                Configuración de Estados
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Seleccionar Proyecto para gestionar estados</InputLabel>
              <Select
                value={selectedProjectForStatus}
                label="Seleccionar Proyecto para gestionar estados"
                onChange={(e) => setSelectedProjectForStatus(e.target.value)}
              >
                <MenuItem value="all">Global (Sistema / Sin Proyecto)</MenuItem>
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedProjectForStatus && (
              <>
            <form onSubmit={handleCreateOrUpdate}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mb: 3 }}
              >
                <TextField
                  label="Nombre Visible"
                  fullWidth
                  value={newStatus.name}
                  onChange={(e) =>
                    setNewStatus({ ...newStatus, name: e.target.value })
                  }
                  required
                />
                <TextField
                  label="Clave Interna"
                  fullWidth
                  value={newStatus.key}
                  onChange={(e) =>
                    setNewStatus({ ...newStatus, key: e.target.value })
                  }
                  required
                  disabled={!!editingId}
                />
                <TextField
                  label="Color"
                  type="color"
                  sx={{ width: { sm: 150 } }}
                  value={newStatus.color}
                  onChange={(e) =>
                    setNewStatus({ ...newStatus, color: e.target.value })
                  }
                  required
                />
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={newStatus.is_initial}
                      onChange={(e) => setNewStatus({ ...newStatus, is_initial: e.target.checked })}
                    />
                  }
                  label="Estado Inicial"
                />
                {editingId && (
                  <Button
                    onClick={() => {
                      setEditingId(null);
                      setNewStatus({ name: "", key: "", color: "#1976d2", is_initial: false });
                    }}
                  >
                    Cancelar
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ minWidth: 150 }}
                >
                  {editingId ? "Actualizar" : "Crear"}
                </Button>
              </Stack>
            </form>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ bgcolor: "action.hover" }}>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Clave</TableCell>
                    <TableCell>Color</TableCell>
                    <TableCell>Inicial</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statuses.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{s.name}</TableCell>
                      <TableCell>
                        <code>{s.key}</code>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={s.name}
                          size="small"
                          sx={{ bgcolor: s.color, color: "#fff" }}
                        />
                      </TableCell>
                      <TableCell>
                        {s.is_initial ? <Chip label="Sí" size="small" color="success" variant="outlined" /> : "No"}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => handleEdit(s)}
                          size="small"
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(s.id)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {statuses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No hay estados configurados en el sistema.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
              </>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Nueva Sección: Asignación de Tickets */}
        <Accordion
          sx={{
            mb: 2,
            borderRadius: "12px !important",
            "&:before": { display: "none" },
          }}
          elevation={2}
          expanded={expandedPanels.assignment}
          onChange={handleAccordionToggle('assignment')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <AssignmentIndIcon color={expandedPanels.assignment ? "primary" : "action"} />
              <Typography variant="h6" fontWeight="600">
                {t('config_assignment_title')}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              {t('config_assignment_help')}
            </Typography>

            <FormControl component="fieldset" sx={{ mb: 4 }}>
              <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                {t('config_assignment_strategy')}
              </FormLabel>
              <RadioGroup
                value={assignmentStrategy}
                onChange={(e) => setAssignmentStrategy(e.target.value)}
              >
                <FormControlLabel 
                  value="VOLUME" 
                  control={<Radio />} 
                  label={t('config_assignment_volume')} 
                />
                <FormControlLabel 
                  value="PROJECT" 
                  control={<Radio />} 
                  label={t('config_assignment_project')} 
                />
              </RadioGroup>
            </FormControl>

            {assignmentStrategy === 'PROJECT' && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Mapeo de Agentes por Proyecto
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead sx={{ bgcolor: 'action.hover' }}>
                      <TableRow>
                        <TableCell sx={{ width: '30%' }}>Proyecto</TableCell>
                        <TableCell>Agentes Asignados</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {projects.map((proj) => (
                        <TableRow key={proj.id}>
                          <TableCell><strong>{proj.name}</strong></TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Autocomplete
                              multiple
                              options={agents}
                              getOptionLabel={(option) => option.full_name || option.email}
                              value={agents.filter(a => (projectAssignments[proj.id] || []).includes(a.id))}
                              onChange={(event, newValue) => {
                                setProjectAssignments({
                                  ...projectAssignments,
                                  [proj.id]: newValue.map(v => v.id)
                                });
                              }}
                              renderInput={(params) => (
                                <TextField 
                                  {...params} 
                                  variant="outlined" 
                                  size="small" 
                                  placeholder="Seleccionar agentes..." 
                                />
                              )}
                              renderTags={(tagValue, getTagProps) =>
                                tagValue.map((option, index) => (
                                  <Chip
                                    label={option.full_name || option.email}
                                    size="small"
                                    {...getTagProps({ index })}
                                  />
                                ))
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleSaveAssignment}
                disabled={isSavingAssignment}
              >
                {isSavingAssignment ? t('profile_save') + '...' : t('config_assignment_save')}
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Sección de Dominios Permitidos */}
        <Accordion
          sx={{
            mb: 2,
            borderRadius: "12px !important",
            "&:before": { display: "none" },
          }}
          elevation={2}
          expanded={expandedPanels.domains}
          onChange={handleAccordionToggle('domains')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LanguageIcon color={expandedPanels.domains ? "primary" : "action"} />
              <Typography variant="h6" fontWeight="600">
                {t('config_domains_title')}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {t('config_domains_help')}
            </Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label={t('config_domains_title')}
                placeholder={t('config_domains_placeholder')}
                value={allowedDomains}
                onChange={(e) => setAllowedDomains(e.target.value)}
                variant="outlined"
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  variant="contained" 
                  onClick={handleSaveAssignment}
                  disabled={isSavingAssignment}
                >
                  {isSavingAssignment ? t('profile_save') + '...' : t('config_domains_save')}
                </Button>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Sección de Roles */}
        <Accordion
          sx={{
            mb: 2,
            borderRadius: "12px !important",
            "&:before": { display: "none" },
          }}
          elevation={2}
          expanded={expandedPanels.roles}
          onChange={handleAccordionToggle('roles')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <SecurityIcon color={expandedPanels.roles ? "primary" : "action"} />
              <Typography variant="h6" fontWeight="600">{t('config_roles_title')}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              {t('config_roles_help')}
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <TextField 
                    size="small" 
                    label="Nombre del nuevo rol" 
                    value={newRoleName} 
                    onChange={(e) => setNewRoleName(e.target.value)}
                />
                <Button variant="contained" onClick={handleCreateRole}>Añadir Rol</Button>
            </Stack>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead sx={{ bgcolor: 'action.hover' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Rol</TableCell>
                            {moduleList.map(m => (
                                <TableCell key={m.key} align="center" sx={{ fontSize: '0.75rem' }}>{m.label}</TableCell>
                            ))}
                            <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rolePermissions.filter(r => r.role_key !== 'ADMIN').map((role) => (
                            <TableRow key={role.id}>
                                <TableCell><strong>{role.role_name}</strong></TableCell>
                                {moduleList.map(m => (
                                    <TableCell key={m.key} align="center">
                                        <Checkbox 
                                            size="small"
                                            checked={role[m.key]} 
                                            onChange={() => handleTogglePermission(role, m.key)}
                                        />
                                    </TableCell>
                                ))}
                                <TableCell align="right">
                                    <IconButton size="small" color="error" onClick={() => handleDeleteRole(role)}>
                                        <DeleteIcon fontSize="inherit" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {rolePermissions.length <= 1 && (
                            <TableRow>
                                <TableCell colSpan={moduleList.length + 2} align="center">
                                    No hay roles adicionales configurados.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Sección de Tiempos de Respuesta (SLA) */}
        <Accordion
          sx={{
            borderRadius: "12px !important",
            "&:before": { display: "none" },
          }}
          elevation={2}
          expanded={expandedPanels.sla}
          onChange={handleAccordionToggle('sla')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TimerIcon color={expandedPanels.sla ? "primary" : "action"} />
              <Typography variant="h6" fontWeight="600">
                Tiempos de Respuesta (SLA)
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Define cuántas horas tiene el equipo para resolver un ticket según
              su prioridad. Esto calculará automáticamente la "Resolución
              Esperada".
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ bgcolor: "action.hover" }}>
                  <TableRow>
                    <TableCell>Prioridad</TableCell>
                    <TableCell>Horas para Resolución</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((prio) => {
                    const sla = slas.find((s) => s.priority === prio);
                    return (
                      <TableRow key={prio}>
                        <TableCell>
                          <strong>{prio}</strong>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={slaValues[prio]}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSlaValues({ ...slaValues, [prio]: val });
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">Horas</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                    variant="contained" 
                    color="primary"
                    onClick={handleSaveSLA}
                    disabled={isSavingSLA}
                >
                    {isSavingSLA ? 'Guardando...' : 'Guardar Tiempos de Respuesta'}
                </Button>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Modal de Configuración de Proyecto y Funcionalidades */}
        <Dialog open={openProjectDialog} onClose={() => setOpenProjectDialog(false)} fullWidth maxWidth="sm">
            <AppDialogTitle onClose={() => setOpenProjectDialog(false)}>
                {editingProjectId ? 'Editar Proyecto' : 'Nuevo Proyecto'}
            </AppDialogTitle>
            <DialogContent>
                <Box component="form" id="project-form" onSubmit={handleProjectSubmit} sx={{ mt: 1 }}>
                    <Stack spacing={3}>
                        <TextField
                            label="Nombre del Proyecto"
                            fullWidth
                            value={newProject.name}
                            onChange={(e) => setNewProject({ name: e.target.value })}
                            required
                        />
                    </Stack>
                </Box>

                {editingProjectId && (
                    <Box sx={{ mt: 4 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Catálogo de Funcionalidades
                        </Typography>
                        
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                            <TextField
                                size="small"
                                placeholder="Buscar funcionalidad..."
                                fullWidth
                                value={funcSearchTerm}
                                onChange={(e) => setFuncSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: funcSearchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setFuncSearchTerm('')} edge="end">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Ordenar por</InputLabel>
                                <Select
                                    value={funcSortBy}
                                    label="Ordenar por"
                                    onChange={(e) => setFuncSortBy(e.target.value)}
                                >
                                    <MenuItem value="date">Más recientes</MenuItem>
                                    <MenuItem value="name">Nombre (A-Z)</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            <TextField
                                size="small"
                                label="Nueva Funcionalidad"
                                fullWidth
                                value={newFuncName}
                                onChange={(e) => setNewFuncName(e.target.value)}
                            />
                            <Button 
                                variant="outlined" 
                                onClick={handleAddFunctionality}
                                disabled={isSavingFunc || !newFuncName.trim()}
                            >
                                Añadir
                            </Button>
                        </Stack>
                        <List>
                            {filteredAndSortedFuncs.map((f) => (
                                <ListItem
                                    key={f.id}
                                    divider
                                    secondaryAction={
                                        <Stack direction="row" spacing={1}>
                                            {editingFuncId === f.id ? (
                                                <>
                                                    <IconButton size="small" color="primary" onClick={() => handleUpdateFunctionality(f.id)}>
                                                        <CheckIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={() => setEditingFuncId(null)}>
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <>
                                                    <IconButton size="small" color="primary" onClick={() => {
                                                        setEditingFuncId(f.id);
                                                        setEditingFuncName(f.name);
                                                    }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" color="error" onClick={() => handleDeleteFunctionality(f.id)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            )}
                                        </Stack>
                                    }
                                >
                                    {editingFuncId === f.id ? (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={editingFuncName}
                                            onChange={(e) => setEditingFuncName(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleUpdateFunctionality(f.id)}
                                            autoFocus
                                        />
                                    ) : (
                                        <ListItemText primary={f.name} />
                                    )}
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenProjectDialog(false)}>Cerrar</Button>
                <Button 
                    type="submit" 
                    form="project-form" 
                    variant="contained"
                >
                    {editingProjectId ? 'Guardar Cambios' : 'Crear Proyecto'}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Diálogo de Migración Masiva */}
        <Dialog
          open={openMigrate}
          onClose={() => {
            setOpenMigrate(false);
            setConfirmText("");
          }}
          fullWidth
          maxWidth="xs"
        >
          <AppDialogTitle onClose={() => {
            setOpenMigrate(false);
            setConfirmText("");
          }}>
            Migración Masiva de Tickets
          </AppDialogTitle>
          <DialogContent>
            <Typography
              variant="body2"
              sx={{ mb: 3, mt: 1 }}
              color="textSecondary"
            >
              Esta herramienta moverá TODOS los tickets de un estado a otro.
              Útil antes de eliminar un estado obsoleto.
            </Typography>
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>Estado de Origen</InputLabel>
                <Select
                  value={migration.from}
                  label="Estado de Origen"
                  onChange={(e) =>
                    setMigration({ ...migration, from: e.target.value })
                  }
                >
                  {statuses.map((s) => (
                    <MenuItem key={s.id} value={s.key}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Estado de Destino</InputLabel>
                <Select
                  value={migration.to}
                  label="Estado de Destino"
                  onChange={(e) =>
                    setMigration({ ...migration, to: e.target.value })
                  }
                >
                  {statuses.map((s) => (
                    <MenuItem key={s.id} value={s.key}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ mb: 1, fontWeight: "bold" }}
                >
                  Para confirmar la operación, escriba "CONFIRMAR":
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="CONFIRMAR"
                  autoComplete="off"
                />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => {
                setOpenMigrate(false);
                setConfirmText("");
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleBulkMigrate}
              variant="contained"
              color="error"
              disabled={
                isMigrating ||
                !migration.from ||
                !migration.to ||
                confirmText !== "CONFIRMAR"
              }
            >
              {isMigrating ? "Migrando..." : "Ejecutar Migración"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal para Eliminar Rol con Migración */}
        <Dialog open={openDeleteRole} onClose={() => setOpenDeleteRole(false)} fullWidth maxWidth="xs">
            <AppDialogTitle onClose={() => setOpenDeleteRole(false)}>
                Eliminar Rol: {roleToDelete?.role_name}
            </AppDialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    ¿Estás seguro de que deseas eliminar este rol?
                </Typography>
                
                {roleToDelete?.user_count > 0 ? (
                    <Box sx={{ mt: 2 }}>
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            Este rol tiene <strong>{roleToDelete.user_count}</strong> usuarios asignados. 
                            Para poder eliminarlo, debes trasladar los usuarios a un rol existente.
                        </Alert>
                        <FormControl fullWidth size="small">
                            <InputLabel>Rol de Destino</InputLabel>
                            <Select
                                value={targetRoleKey}
                                label="Rol de Destino"
                                onChange={(e) => setTargetRoleKey(e.target.value)}
                            >
                                {/* Incluimos ADMIN manualmente por si acaso, y filtramos el actual */}
                                <MenuItem value="ADMIN">ADMINISTRADOR</MenuItem>
                                {rolePermissions
                                    .filter(r => r.role_key !== roleToDelete.role_key)
                                    .map(r => (
                                        <MenuItem key={r.id} value={r.role_key}>{r.role_name}</MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                    </Box>
                ) : (
                    <Typography variant="body2" color="textSecondary">
                        Este rol no tiene usuarios asignados. Se puede eliminar de forma segura.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setOpenDeleteRole(false)}>Cancelar</Button>
                <Button 
                    variant="contained" 
                    color="error" 
                    onClick={handleConfirmDeleteRole}
                    disabled={roleToDelete?.user_count > 0 && !targetRoleKey}
                >
                    {roleToDelete?.user_count > 0 ? 'Trasladar y Eliminar' : 'Eliminar Rol'}
                </Button>
            </DialogActions>
        </Dialog>
      </Box>
    );
};

export default Configuration;